import React from "react";

import { Checkbox } from "~/components/checkbox";
import { CheckboxLabel } from "~/components/checkbox/label";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormMessage } from "~/components/form/message";
import { FormFieldReset } from "~/components/form/reset";
import { IconInfoItalicOutlined } from "~/components/icon";
import { Input } from "~/components/input";
import { Star } from "~/components/star";
import { Textarea } from "~/components/textarea";
import { cx } from "~/tools/class-variance";
import { matchIdentifiable } from "~/tools/identifiable";
import { not } from "~/tools/not";
import { onIdle } from "~/tools/on-idle";

import { queryAllDashboards } from "../../queries/dashboards";
import { useQuery } from "../../queries/react-query";
import { DASHBOARD_PATH } from "../../routes/Routes";
import { useErrorMessage } from "../_notification-error";
import { Link, useNavigateTo, useNavigateToLinkLazy } from "../react/link";
import { notification } from "../react/notification/Notification";
import { HelpPopupTooltip } from "../react/tooltip/HelpPopupTooltip";

import type { SetupDashboardDrawerState } from "./_setup-dashboard-drawer-state";
import { useSetupDashboard } from "./_use-setup-dashboard";

export interface SetupDashboardFormProps {
	formId: string;
	state: SetupDashboardDrawerState;
	close: VoidFunction;

	canSave?: boolean;
	className?: string;
}

export const SetupDashboardForm: React.FC<SetupDashboardFormProps> = ({
	formId,
	state,
	close,
	canSave = true,
	className,
}) => {
	const navigateTo = useNavigateTo();
	const navigateToLink = useNavigateToLinkLazy();
	const nameInputRef = React.useRef<HTMLInputElement>(null);
	const { displayErrorMessage } = useErrorMessage();

	const { data: usedDashboardNames } = useQuery({
		...queryAllDashboards,
		// do not refetch - the cached data is alright
		refetchOnMount: false,
		select: (dashboards) => {
			const otherDashboards = state.type === "edit" ? dashboards.filter(not(matchIdentifiable(state.id))) : dashboards;

			return new Set(otherDashboards.map(({ name }) => name));
		},
	});

	const { createDashboard, cloneDashboard, editDashboard } = useSetupDashboard({
		onSuccessfulCreate: (dashboard) => {
			if (state.type === "create" && state.onSuccess != null) {
				state.onSuccess(dashboard);
			} else {
				navigateTo(DASHBOARD_PATH, {
					params: { idOrIdentifier: dashboard.id },
					search: ["timeRange", "timestamp", "detachedFilters"],
				});
			}
		},

		onSuccessfulClone: ({ id, name }) => {
			const destination = navigateToLink(DASHBOARD_PATH, {
				params: { idOrIdentifier: id },
				search: ["timeRange", "timestamp", "detachedFilters"],
			});

			notification.success({
				message: (
					<>
						The dashboard has been successfully saved as{" "}
						{/* it can use Router.Link here because the notification is out of Router context */}
						<Link el="a" href={`#${destination}`} theme="secondary">
							{name}
						</Link>
					</>
				),
			});
		},

		onSuccessfulEdit: (name) => {
			notification.success({
				message: `Dashboard "${name}" has been successfully updated.`,
			});
		},

		onSuccessfulStar: () => {
			notification.success({
				message: "Dashboard has been successfully updated.",
			});
		},

		onFailedStar: (error) => {
			displayErrorMessage({
				message: "Something went wrong while starring a dashboard",
				error,
			});
		},
	});

	React.useEffect(() => {
		// the drawer does not allow to autoFocus the input
		// neither .focus() during synchronous effect
		return onIdle(() => {
			nameInputRef.current?.focus();
		});
	}, []);

	return (
		<Form
			className={cx("space-y-6", className)}
			id={formId}
			onSubmit={async (values) => {
				if (!canSave) {
					return;
				}

				if (state.type === "create") {
					try {
						await createDashboard(state.widgets ?? [], values);
						// wait for the dashboard to be created before closing
						close();
					} catch {
						// the failed Promise is handled in the hook
					}
				} else if (state.type === "clone") {
					try {
						await cloneDashboard(state.id, state.spec, values);
						// wait for the dashboard to be cloned before closing
						close();
					} catch {
						// the failed Promise is handled in the hook
					}
				} else {
					editDashboard(state.id, state.form, values);

					// close the drawer immediately
					close();
				}
			}}
			state={state.form}
		>
			<FormField state={state.form.fields.dashboard.fields.name}>
				<FormLabel asChild>
					<div>
						<FormLabel>Name *</FormLabel>
						{state.type !== "create" && <FormFieldReset />}
					</div>
				</FormLabel>

				{(monitor, field) => (
					<Input
						name="dashboard-name"
						onChange={(event) => field.setInput(event.target.value)}
						placeholder="Keep it short and sweet"
						ref={nameInputRef}
						value={field.getInput(monitor)}
					/>
				)}

				{(monitor, field) => {
					// when creating show only when the field is validated
					if (state.type === "create" && !field.isValidated(monitor)) {
						return null;
					}

					const dashboardName = field.getOutput(monitor);

					if (dashboardName == null || !usedDashboardNames?.has(dashboardName)) {
						return null;
					}

					return (
						<FormMessage className="flex items-center gap-1 font-light text-grey-600 italic">
							<IconInfoItalicOutlined className="text-(--base-blue-600)" />
							This name is already in use
						</FormMessage>
					);
				}}
			</FormField>

			<FormField state={state.form.fields.dashboard.fields.description}>
				<FormLabel asChild>
					<div>
						<FormLabel>Description</FormLabel>
						{state.type !== "create" && <FormFieldReset />}
					</div>
				</FormLabel>

				{(monitor, field) => (
					<Textarea
						name="dashboard-description"
						onChange={(event) => field.setInput(event.target.value)}
						placeholder="e.g. standard DevOps investigation widgets"
						rows={5}
						value={field.getInput(monitor)}
					/>
				)}
			</FormField>

			<div className="space-y-3">
				<FormField state={state.form.fields.options}>
					<FormLabel asChild>
						<div>
							<FormLabel>Options</FormLabel>
							{state.type !== "create" && <FormFieldReset />}
						</div>
					</FormLabel>
				</FormField>

				<div className="space-y-2">
					<FormField state={state.form.fields.options.fields.starred}>
						{(monitor, field) => {
							const starred = field.getInput(monitor);

							return (
								<CheckboxLabel className="flex items-center gap-2">
									<Checkbox
										checked={starred}
										name="dashboard-starred"
										onCheckedChange={(checked) => field.setInput(checked)}
									/>
									<span className="leading-[21px]">Add it to starred dashboards</span>
									<Star className="-m-0.5" starred={starred} />
								</CheckboxLabel>
							);
						}}
					</FormField>

					<FormField state={state.form.fields.options.fields.publiclyAccessible}>
						{(monitor, field) => {
							const publiclyAccessible = field.getInput(monitor);

							return (
								<div className="flex items-center gap-2">
									<CheckboxLabel className="flex items-center gap-2">
										<Checkbox
											checked={publiclyAccessible}
											name="dashboard-publicly-accessible"
											onCheckedChange={(checked) => field.setInput(checked)}
										/>
										<span className="leading-[21px]">Make it public</span>
										<HelpPopupTooltip content="By default, all the dashboards you create are private (only you can see them). By making a dashboard public, you still own the dashboard but other users can also view it." />
									</CheckboxLabel>
								</div>
							);
						}}
					</FormField>
				</div>
			</div>

			<blockquote className="m-0 text-13/[21px] text-grey-600 italic tracking-tight">
				<strong className="font-semibold">Pro tip</strong>
				<br />
				If you frequently use a dashboard, you should star it - all the starred dashboards are listed directly under the
				Dashboards section of the main menu for quick access.
			</blockquote>
		</Form>
	);
};
